import React, { useState, useEffect, useContext, useCallback } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { FileDown, RefreshCw, TrendingUp, TrendingDown, Minus, ExternalLink } from 'lucide-react';
import { AuthContext } from '@/App';
import { useTheme } from '@/contexts/ThemeContext';
import { useWorkspace } from '@/contexts/WorkspaceContext';
import DateRangePicker, { getDateRange } from '@/components/DateRangePicker';
import DashboardLayout from '@/components/DashboardLayout';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PRESET_MAP = {
  'Hoy': 'today', 'Ayer': 'yesterday',
  'Últimos 7 días': '7d', 'Últimos 15 días': '15d',
  'Últimos 30 días': '30d', 'Últimos 90 días': '90d',
  'Este mes': 'this_month', 'Mes anterior': 'last_month',
};

const fmt = n => !n ? '0' : n >= 1000000 ? `${(n/1000000).toFixed(1)}M` : n >= 1000 ? `${(n/1000).toFixed(1)}K` : n.toLocaleString();
const fmtUsd = n => !n ? '$0' : n >= 1000000 ? `$${(n/1000000).toFixed(2)}M` : n >= 1000 ? `$${(n/1000).toFixed(1)}K` : `$${Number(n).toFixed(2)}`;

const ESTADO_CONFIG = {
  'Mantener': { color: 'bg-green-100 text-green-700 border-green-200', icon: TrendingUp },
  'Optimizar': { color: 'bg-yellow-100 text-yellow-700 border-yellow-200', icon: Minus },
  'Pausar': { color: 'bg-red-100 text-red-700 border-red-200', icon: TrendingDown },
};

// Agrupar por objetivo Meta
const GRUPO_MAP = {
  OUTCOME_TRAFFIC: 'Tráfico', LINK_CLICKS: 'Tráfico', CONVERSIONS: 'Tráfico', OUTCOME_SALES: 'Tráfico',
  OUTCOME_ENGAGEMENT: 'Interacción', OUTCOME_AWARENESS: 'Interacción', VIDEO: 'Interacción',
  MESSAGES: 'Mensajes', OUTCOME_LEADS: 'Mensajes',
};
const grupoDe = (obj, name='', platform='') => {
  if ((platform||'').toLowerCase() === 'google') return { grupo: 'Google Ads', esWsp: false };
  const wsp = /whatsapp|wsp|wpp|wsapp|wasap|mensaje|api\.whatsapp/i.test(name);
  if (wsp) return { grupo: 'WhatsApp', esWsp: true };
  const g = GRUPO_MAP[obj] || 'Otros';
  return { grupo: g, esWsp: false };
};

// Reglas de recomendación (umbrales reales Oncovix: CTR p25=2.65 p75=4.67 · costo/conv p75=1719)
const evaluar = (c) => {
  const ctr = c.ctr || 0;
  const cpa = (c.conversions > 0) ? (c.spent / c.conversions) : null;
  if (ctr >= 4.67 && (cpa === null || cpa <= 143))
    return { estado: 'Mantener', reco: 'Buena campaña: CTR alto y costo eficiente. Mantener.' };
  if (ctr < 2.65 || (cpa !== null && cpa > 1719))
    return { estado: 'Pausar', reco: 'Bajo rendimiento. Revisar creativo o pausar.' };
  return { estado: 'Optimizar', reco: 'Rendimiento medio. Optimizar segmentación o creativo.' };
};

export default function CampaignMatrix() {
  const { token } = useContext(AuthContext);
  const theme = useTheme();
  const { activeWorkspace, clientId } = useWorkspace();
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState(() => getDateRange('30d'));
  const [generatingPdf, setGeneratingPdf] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [grupoActivo, setGrupoActivo] = useState('Todos');

  const fetchCampaigns = useCallback(async (wsId, rangeLabel) => {
    if (!wsId) { setLoading(false); return; }
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const preset = PRESET_MAP[rangeLabel] || '30d';
      const cp = wsId ? `&client_id=${wsId}` : '';
      // Convert dateRange label to date_from/date_to
      const today = new Date();
      const fmt = d => d.toISOString().split('T')[0];
      const presetDates = {
        'Hoy': { from: fmt(today), to: fmt(today) },
        'Ayer': { from: fmt(new Date(today-86400000)), to: fmt(new Date(today-86400000)) },
        'Últimos 7 días': { from: fmt(new Date(today-6*86400000)), to: fmt(today) },
        'Últimos 15 días': { from: fmt(new Date(today-14*86400000)), to: fmt(today) },
        'Últimos 30 días': { from: fmt(new Date(today-29*86400000)), to: fmt(today) },
        'Últimos 90 días': { from: fmt(new Date(today-89*86400000)), to: fmt(today) },
        'Este mes': { from: fmt(new Date(today.getFullYear(), today.getMonth(), 1)), to: fmt(today) },
        'Mes anterior': { from: fmt(new Date(today.getFullYear(), today.getMonth()-1, 1)), to: fmt(new Date(today.getFullYear(), today.getMonth(), 0)) },
      };
      const dates = presetDates[rangeLabel] || { from: fmt(new Date(today-29*86400000)), to: fmt(today) };
      const res = await axios.get(`${API}/dashboard/metrics?client_id=${wsId}&date_from=${dates.from}&date_to=${dates.to}`, { headers });
      let camps = res.data?.campaigns || [];
      // Cargar creativos (imagen + link) y cruzarlos por id de campaña
      try {
        const cr = await axios.get(`${API}/creatives?client_id=${wsId}`, { headers });
        const byCamp = cr.data?.by_campaign || {};
        camps = camps.map(c => {
          const ads = byCamp[c.id] || byCamp[String(c.id)] || [];
          const withImg = ads.find(a => a.thumbnail_url) || ads[0];
          return withImg ? { ...c, creatives: ads, thumbnail: withImg.thumbnail_url, preview_link: withImg.preview_link, instagram_permalink: withImg.instagram_permalink, post_permalink: withImg.post_permalink, ad_copy: withImg.body } : { ...c, creatives: ads };
        });
      } catch(e) { /* si falla, muestra métricas sin imagen */ }
      // Traer objetivos y clasificar en grupo + estado + recomendación
      try {
        const oj = await axios.get(`${API}/campaign-objectives?client_id=${wsId}`, { headers });
        const objs = oj.data?.objectives || {};
        camps = camps.map(c => {
          const obj = objs[c.id] || objs[String(c.id)] || null;
          const { grupo, esWsp } = grupoDe(obj, c.name, c.platform);
          const ev = evaluar(c);
          return { ...c, objective: obj, grupo, esWsp, estado: ev.estado, reco: ev.reco };
        });
      } catch(e) { /* sin objetivos, sigue sin agrupar */ }
      setCampaigns(camps);
      setLastUpdate(new Date().toLocaleString('es-CL'));
    } catch(e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchCampaigns(clientId, dateRange.label);
  }, [clientId, dateRange.label, fetchCampaigns]);

  const generatePDF = async () => {
    setGeneratingPdf(true);
    const el = document.getElementById('matriz-pdf');
    const header = el ? el.querySelector('.pdf-only') : null;
    const sinImg = el ? Array.from(el.querySelectorAll('.sin-img-ph')) : [];
    try {
      if (header) header.style.display = 'block';
      sinImg.forEach(x => { x.style.visibility = 'hidden'; });
      const { default: html2pdf } = await import('html2pdf.js');
      await html2pdf().set({
        margin: 8,
        filename: `Matriz_Campañas_${activeWorkspace?.name || 'AdPulse'}_${new Date().toISOString().split('T')[0]}.pdf`,
        image: { type: 'jpeg', quality: 0.95 },
        html2canvas: { scale: 2, useCORS: true, windowWidth: 1500, scrollX: 0, scrollY: 0 },
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'landscape' }
      }).from(el).save();
    } catch(e) {
      console.error('PDF error:', e);
    } finally {
      if (header) header.style.display = 'none';
      sinImg.forEach(x => { x.style.visibility = ''; });
      setGeneratingPdf(false);
    }
  };

  const byEstado = { Mantener: campaigns.filter(c=>c.estado==='Mantener'), Optimizar: campaigns.filter(c=>c.estado==='Optimizar'), Pausar: campaigns.filter(c=>c.estado==='Pausar') };

  return (
    <DashboardLayout>
      <div className="p-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className={`font-heading font-bold text-3xl ${theme.text}`}>Matriz de Campañas</h1>
            <p className={`text-sm ${theme.textSecondary} mt-1`}>
              {activeWorkspace?.name || 'Todas las cuentas'} · {dateRange.label}
              {lastUpdate && <span className="ml-2">· Actualizado: {lastUpdate}</span>}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <DateRangePicker value={dateRange} onChange={setDateRange} />
            <button onClick={() => fetchCampaigns(clientId, dateRange.label)}
              className="flex items-center gap-2 px-4 py-2 rounded-xl border border-gray-200 text-sm font-medium hover:bg-gray-50 transition-all">
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              Actualizar
            </button>
            <button onClick={generatePDF} disabled={generatingPdf}
              className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-xl text-sm font-semibold hover:bg-indigo-700 transition-all disabled:opacity-50">
              <FileDown className="w-4 h-4" />
              {generatingPdf ? 'Generando...' : 'Exportar PDF'}
            </button>
          </div>
        </div>

        {/* Pestañas por grupo */}
        <div className="flex flex-wrap gap-2 mb-6">
          {['Todos','Tráfico','WhatsApp','Interacción','Mensajes','Google Ads','Otros'].map(g => {
            const n = g==='Todos' ? campaigns.length : campaigns.filter(x=>x.grupo===g).length;
            if (g!=='Todos' && n===0) return null;
            const activo = grupoActivo===g;
            return (
              <button key={g} onClick={()=>setGrupoActivo(g)}
                className={`px-4 py-2 rounded-xl text-sm font-medium transition-all border ${activo ? 'bg-indigo-600 text-white border-indigo-600' : `${theme.glassCard} ${theme.textSecondary} border-gray-200 hover:bg-gray-50`}`}>
                {g} <span className={`ml-1 text-xs ${activo?'text-indigo-200':'opacity-60'}`}>({n})</span>
              </button>
            );
          })}
        </div>
        {/* Resumen del grupo activo */}
        {grupoActivo!=='Todos' && (() => {
          const g = campaigns.filter(x=>x.grupo===grupoActivo);
          if (!g.length) return null;
          const gasto = g.reduce((s,x)=>s+(x.spent||0),0);
          const conv = g.reduce((s,x)=>s+(x.conversions||0),0);
          const mejor = [...g].sort((a,b)=>(b.conversions||0)-(a.conversions||0))[0];
          return (
            <div className={`${theme.glassCard} p-4 rounded-2xl mb-6 flex flex-wrap gap-6 items-center`}>
              <div><p className={`text-xs ${theme.textSecondary}`}>Gasto total</p><p className={`text-xl font-bold ${theme.text}`}>{fmtUsd(gasto)}</p></div>
              <div><p className={`text-xs ${theme.textSecondary}`}>Conversiones</p><p className={`text-xl font-bold text-green-600`}>{fmt(conv)}</p></div>
              <div className="flex-1"><p className={`text-xs ${theme.textSecondary}`}>🏆 Mejor campaña del grupo</p><p className={`text-sm font-semibold ${theme.text} truncate`}>{mejor?.name} · {(mejor?.ctr||0).toFixed(2)}% CTR · {Math.round(mejor?.conversions||0)} conv.</p></div>
            </div>
          );
        })()}


        {/* PDF Content */}
        <div id="matriz-pdf">
          {/* PDF Header */}
          <div className="bg-indigo-600 text-white p-6 rounded-2xl mb-6 pdf-only" style={{display:'none'}}>
            <h2 className="text-2xl font-bold">{activeWorkspace?.name || 'AdPulse AI'} · Matriz de Campañas</h2>
            <p className="text-indigo-200 mt-1">Período: {dateRange.label} · Generado el {new Date().toLocaleString('es-CL')}</p>
          </div>

          {loading ? (
            <div className="text-center py-20">
              <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-3 text-indigo-500" />
              <p className={theme.textSecondary}>Cargando campañas desde Meta API...</p>
            </div>
          ) : campaigns.length === 0 ? (
            <div className={`${theme.glassCard} p-12 rounded-2xl text-center`}>
              <p className={theme.textSecondary}>No hay campañas para mostrar en este período</p>
            </div>
          ) : (
            <div className={`${theme.glassCard} rounded-2xl overflow-hidden`}>
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-indigo-600 text-white">
                    <th className="py-4 px-4 text-left font-semibold">Miniatura</th>
                    <th className="py-4 px-4 text-left font-semibold">Campaña</th>
                    <th className="py-4 px-4 text-right font-semibold">Gasto</th>
                    <th className="py-4 px-4 text-right font-semibold">Impresiones</th>
                    <th className="py-4 px-4 text-right font-semibold">Clicks</th>
                    <th className="py-4 px-4 text-right font-semibold">CTR</th>
                    <th className="py-4 px-4 text-right font-semibold">Conv.</th>
                    <th className="py-4 px-4 text-left font-semibold">Recomendación</th>
                    <th className="py-4 px-4 text-center font-semibold">Link</th>
                  </tr>
                </thead>
                <tbody>
                  {campaigns.filter(c => grupoActivo==='Todos' || c.grupo===grupoActivo).sort((a,b)=>{const s=(x)=>{const g=(x.platform||'').toLowerCase()==='google';if(g&&/youtube|video/i.test(x.name||''))return 2;if(g)return 1;return 0;};return s(a)-s(b);}).map((c, i, arr) => {
                    const secDe=(x)=>{const g=(x.platform||'').toLowerCase()==='google';if(g&&/youtube|video/i.test(x.name||''))return 'YouTube';if(g)return 'Google Ads';return 'Meta Ads';};
                    const secActual=secDe(c);
                    const secAnterior=i>0?secDe(arr[i-1]):null;
                    const mostrarHeader=secActual!==secAnterior;
                    const cfg = ESTADO_CONFIG[c.estado] || ESTADO_CONFIG['Optimizar'];
                    const icono = secActual==='Meta Ads'?'📘':secActual==='Google Ads'?'🔍':'▶️';
                    return (
                    <React.Fragment key={'fr-'+(c.id||i)}>
                    {mostrarHeader && (
                      <tr style={{background:'#4f46e5'}}>
                        <td colSpan={8} style={{padding:'8px 16px',fontWeight:'bold',color:'#fff',fontSize:'14px'}}>{icono} {secActual}</td>
                      </tr>
                    )}
                      <motion.tr key={c.id||i} initial={{opacity:0}} animate={{opacity:1}} transition={{delay:i*0.02}}
                        className={`border-b ${theme.border} hover:bg-white/5 transition-colors ${i%2===0?'':'bg-white/2'}`}>
                        <td className="py-3 px-4">
                          {c.thumbnail ? (
                            <a href={c.preview_link || c.thumbnail} target="_blank" rel="noreferrer">
                            <img src={c.thumbnail} alt="" className="w-24 h-16 object-cover rounded-lg border border-gray-200 hover:ring-2 hover:ring-blue-400 transition" referrerPolicy="no-referrer" />
                          </a>
                          ) : (
                            <div className="sin-img-ph w-24 h-16 bg-gray-200 rounded-lg flex items-center justify-center text-xs text-gray-400">Sin img</div>
                          )}
                        </td>
                        <td className="py-3 px-4" style={{minWidth:'320px',maxWidth:'340px'}}>
                          <p className={`font-medium ${theme.text}`} style={{lineHeight:'1.25',marginBottom:'3px',wordBreak:'break-word'}}>{c.name}</p>
                          {c.ad_copy && <p className={`text-xs ${theme.textSecondary}`} style={{lineHeight:'1.2',wordBreak:'break-word'}}>{c.ad_copy.substring(0,70)}…</p>}
                        </td>
                        <td className={`py-3 px-4 text-right font-semibold text-blue-600`}>{fmtUsd(c.spent)}</td>
                        <td className={`py-3 px-4 text-right ${theme.textSecondary}`}>{fmt(c.impressions)}</td>
                        <td className={`py-3 px-4 text-right ${theme.textSecondary}`}>{fmt(c.clicks)}</td>
                        <td className={`py-3 px-4 text-right text-indigo-500 font-medium`}>{(c.ctr||0).toFixed(2)}%</td>
                        <td className={`py-3 px-4 text-right text-green-600 font-semibold`}>{fmt(Math.round(c.conversions||0))}</td>
                        <td className="py-3 px-4 max-w-[200px]">
                          <span className={`inline-block px-2 py-1 rounded-lg text-xs font-medium ${c.estado==='Mantener'?'bg-green-50 text-green-700':c.estado==='Pausar'?'bg-red-50 text-red-700':'bg-yellow-50 text-yellow-700'}`}>{c.reco || '—'}</span>
                          {c.esWsp && <span className="ml-1 text-xs">💬</span>}
                        </td>
                        <td className="py-3 px-4 text-center">
                          {(c.preview_link || c.id) ? (
                            <a href={c.post_permalink || c.instagram_permalink || c.preview_link || `https://www.facebook.com/ads/library/?id=${c.id}`}
                              target="_blank" rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-blue-50 text-blue-600 hover:bg-blue-100 text-xs font-medium transition-all">
                              <ExternalLink className="w-3 h-3" /> Ver
                            </a>
                          ) : '—'}
                        </td>
                      </motion.tr>
                    {secActual==='YouTube' && Array.isArray(c.creatives) && c.creatives.length > 0 && c.creatives.filter(v => !v.effective_status || v.effective_status === 'ENABLED').map((v, vi) => (
                      <tr key={(c.id||i)+'-v-'+vi} className={`border-b ${theme.border}`} style={{background:'rgba(0,0,0,0.02)'}}>
                        <td className="py-2 px-4" style={{paddingLeft:'2rem'}}>
                          {v.thumbnail_url ? (
                            <a href={v.post_permalink || v.preview_link || v.thumbnail_url} target="_blank" rel="noreferrer">
                              <img src={v.thumbnail_url} alt="" className="w-20 h-12 object-cover rounded-md" referrerPolicy="no-referrer" />
                            </a>
                          ) : (
                            <div className="w-20 h-12 bg-gray-200 rounded-md flex items-center justify-center text-xs text-gray-400">Sin img</div>
                          )}
                        </td>
                        <td className="py-2 px-4" style={{minWidth:'320px',maxWidth:'340px'}}>
                          <p className={`text-xs ${theme.textSecondary}`} style={{lineHeight:'1.2',wordBreak:'break-word'}}>{v.ad_name || v.body || 'Video'}</p>
                        </td>
                        <td colSpan={6}></td>
                        <td className="py-2 px-4 text-center">
                          {(v.post_permalink || v.preview_link) ? (
                            <a href={v.post_permalink || v.preview_link} target="_blank" rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-blue-50 text-blue-600 hover:bg-blue-100 text-xs font-medium transition-all">
                              <ExternalLink className="w-3 h-3" /> Ver
                            </a>
                          ) : '-'}
                        </td>
                      </tr>
                    ))}
                    </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}
